import React from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
} from '@mui/material';

export interface SearchableSelectOption {
  value: string | number;
  label: string;
  /** Optional second line, shown in small secondary type inside the list. */
  secondary?: string;
}

interface SearchableSelectProps {
  label: string;
  value: string | number;
  options: SearchableSelectOption[];
  onChange: (value: string | number) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  includeAllOption?: boolean;
  allLabel?: string;
  allValue?: string | number;
  placeholder?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  value,
  options,
  onChange,
  fullWidth = true,
  disabled = false,
  size = 'medium',
  includeAllOption = false,
  allLabel,
  allValue = '',
  placeholder,
}) => {
  const allOption: SearchableSelectOption | null = includeAllOption
    ? { value: allValue, label: allLabel || `All ${label}` }
    : null;
  const mergedOptions: SearchableSelectOption[] = allOption ? [allOption, ...options] : options;
  // Treat "All" as no selection so the search placeholder stays visible
  // (same UX as Employee Autocomplete filters elsewhere).
  const isAllSelected =
    includeAllOption &&
    (value === allValue || value === '' || value == null);
  const selectedOption = isAllSelected
    ? null
    : mergedOptions.find(option => String(option.value) === String(value)) || null;
  const searchPlaceholder =
    placeholder || `Search ${label.toLowerCase()}...`;

  return (
    <Autocomplete<SearchableSelectOption>
      options={mergedOptions}
      value={selectedOption}
      onChange={(_, newValue) => onChange(newValue?.value ?? allValue)}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, selected) =>
        Boolean(option && selected) && String(option.value) === String(selected.value)
      }
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      noOptionsText="No results found"
      clearOnEscape
      selectOnFocus
      handleHomeEndKeys
      autoHighlight
      // Keep listbox in-document for stable Jest/jsdom rendering.
      disablePortal
      filterOptions={(options: SearchableSelectOption[], { inputValue }) => {
        if (!inputValue) return options;
        const searchLower = inputValue.toLowerCase();
        return options.filter((option: SearchableSelectOption) => {
          const haystack = [option.label, option.secondary, String(option.value)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(searchLower);
        });
      }}
      renderOption={(props, option: SearchableSelectOption) => (
        <li {...props} key={String(option.value)}>
          {option.secondary ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
              <Box component="span">{option.label}</Box>
              <Typography variant="caption" color="text.secondary" component="span" sx={{ lineHeight: 1.2 }}>
                {option.secondary}
              </Typography>
            </Box>
          ) : (
            option.label
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={searchPlaceholder}
        />
      )}
    />
  );
};

export default SearchableSelect;
